import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import axios from '../../../utils/axios';

const AssetRecoveryView = () => {
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('recovery_view_mode') || 'table';
    } catch {
      return 'table';
    }
  });

  const handleSetViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('recovery_view_mode', mode);
    } catch {
      // ignore
    }
  };

  // Create Form State
  const [createForm, setCreateForm] = useState({
    user_management_id: '',
    name: '',
    email: '',
    department: 'Engineering',
    role: 'Staff Engineer',
    manager: 'Department Lead',
    last_working_day: new Date().toISOString().split('T')[0],
    exit_type: 'Resignation',
    priority: 'Medium',
    notes: 'Offboarding clearance initiated.',
    send_email: true,
  });

  // Fetch clearances, users, and assets
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [clearanceRes, userRes, assetRes] = await Promise.all([
        axios.get('/asset-clearances'),
        axios.get('/admin/users').catch(() => ({ data: [] })),
        axios.get('/assets').catch(() => ({ data: { data: [] } })),
      ]);

      if (clearanceRes.data?.success) {
        setEmployees(clearanceRes.data.data || []);
      }
      setUsers(Array.isArray(userRes.data) ? userRes.data : (userRes.data?.data || []));
      setAllAssets(assetRes.data?.data || []);
    } catch (err) {
      console.error('Error fetching clearance data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keep selectedEmployee synced when employees update
  useEffect(() => {
    if (selectedEmployee) {
      const fresh = employees.find((e) => e.id === selectedEmployee.id);
      if (fresh) setSelectedEmployee(fresh);
    }
  }, [employees, selectedEmployee]);

  // Live Metrics computation
  const metrics = useMemo(() => {
    let pendingCases = 0;
    let completedCases = 0;
    let totalHardware = 0;
    let recoveredHardware = 0;
    let totalAccess = 0;
    let revokedAccess = 0;
    let totalLicenceCost = 0;
    let recoveredLicenceCost = 0;

    employees.forEach((emp) => {
      if (emp.status === 'Completed') completedCases++;
      else pendingCases++;

      (emp.hardware_assets || []).forEach((a) => {
        totalHardware++;
        if (a.status === 'Recovered') recoveredHardware++;
      });

      (emp.access_items || []).forEach((acc) => {
        totalAccess++;
        if (acc.status === 'Revoked') revokedAccess++;
      });

      (emp.licences || []).forEach((lic) => {
        const cost = Number(lic.cost || 0);
        totalLicenceCost += cost;
        if (['Reassigned', 'Released'].includes(lic.status)) {
          recoveredLicenceCost += cost;
        }
      });
    });

    return {
      pendingCases,
      completedCases,
      totalHardware,
      recoveredHardware,
      hardwarePct: totalHardware ? Math.round((recoveredHardware / totalHardware) * 100) : 0,
      totalAccess,
      revokedAccess,
      accessPct: totalAccess ? Math.round((revokedAccess / totalAccess) * 100) : 0,
      totalLicenceCost,
      recoveredLicenceCost,
    };
  }, [employees]);

  // Filtered employees
  const filteredEmployees = employees.filter((emp) => {
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'pending' && emp.status !== 'Completed') ||
      (filterStatus === 'completed' && emp.status === 'Completed');
    const q = search.toLowerCase().trim();
    const matchesSearch =
      !q ||
      (emp.name && emp.name.toLowerCase().includes(q)) ||
      (emp.employee_id && emp.employee_id.toLowerCase().includes(q)) ||
      (emp.clearance_id && emp.clearance_id.toLowerCase().includes(q)) ||
      (emp.department && emp.department.toLowerCase().includes(q));
    return matchesStatus && matchesSearch;
  });

  const handleOpenChecklist = (employee) => {
    setSelectedEmployee(employee);
    setShowDrawer(true);
  };

  // 1. Toggle Hardware Recovery (Updates DB and Asset Table!)
  const toggleAssetRecovery = async (assetIdentifier, newCondition = 'Optimal') => {
    if (!selectedEmployee) return;
    try {
      setActionLoading(true);
      const res = await axios.post(`/asset-clearances/${selectedEmployee.id}/toggle-recovery`, {
        asset_id: assetIdentifier,
        condition: newCondition,
        send_email: true,
      });

      setAlertMessage({
        type: 'success',
        text: `Asset updated! Returned to inventory stock & receipt emailed to ${selectedEmployee.email}.`,
      });

      await fetchData();
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (err) {
      console.error(err);
      setAlertMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to update asset recovery status.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // 2. Toggle System Access Revocation
  const toggleAccessRevocation = async (accessId) => {
    if (!selectedEmployee) return;
    try {
      setActionLoading(true);
      await axios.post(`/asset-clearances/${selectedEmployee.id}/toggle-access`, {
        access_id: accessId,
      });
      await fetchData();
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (err) {
      console.error(err);
      setAlertMessage({ type: 'error', text: 'Failed to update access status.' });
    } finally {
      setActionLoading(false);
    }
  };

  // 3. Toggle Licence Reassignment
  const toggleLicenceStatus = async (licId) => {
    if (!selectedEmployee) return;
    try {
      setActionLoading(true);
      await axios.post(`/asset-clearances/${selectedEmployee.id}/toggle-licence`, {
        licence_id: licId,
      });
      await fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  // 4. Toggle Department Sign-Off
  const toggleSignoff = async (deptKey) => {
    if (!selectedEmployee) return;
    try {
      setActionLoading(true);
      await axios.post(`/asset-clearances/${selectedEmployee.id}/toggle-signoff`, {
        department: deptKey,
      });
      setAlertMessage({
        type: 'success',
        text: `${deptKey.toUpperCase()} Clearance sign-off updated!`,
      });
      await fetchData();
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (err) {
      console.error(err);
      setAlertMessage({ type: 'error', text: 'Failed to update sign-off.' });
    } finally {
      setActionLoading(false);
    }
  };

  // Complete Clearance and Issue Certificate
  const handleCompleteClearance = async () => {
    if (!selectedEmployee) return;
    try {
      setActionLoading(true);
      const res = await axios.post(`/asset-clearances/${selectedEmployee.id}/complete`, {
        completed_by: 'IT Operations Administrator',
        send_email: true,
      });

      setAlertMessage({
        type: 'success',
        text: `Exit clearance certificate ${res.data.data?.certificate_number} generated & emailed to ${selectedEmployee.email}!`,
      });

      await fetchData();
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
      setShowCertificateModal(true);
    } catch (err) {
      console.error(err);
      setAlertMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to complete clearance.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Send Pre-paid Return Kit Email
  const handleSendReturnKit = async () => {
    if (!selectedEmployee) return;
    try {
      setActionLoading(true);
      const res = await axios.post(`/asset-clearances/${selectedEmployee.id}/send-return-kit`, {
        courier: 'BlueDart Express / FedEx',
      });

      setAlertMessage({
        type: 'success',
        text: `Pre-paid return kit & courier instructions emailed to ${selectedEmployee.email} (Tracking: ${res.data.tracking_number})!`,
      });
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (err) {
      console.error(err);
      setAlertMessage({ type: 'error', text: 'Failed to send return kit instructions.' });
    } finally {
      setActionLoading(false);
    }
  };

  // Send Reminder Email
  const handleSendReminder = async () => {
    if (!selectedEmployee) return;
    try {
      setActionLoading(true);
      await axios.post(`/asset-clearances/${selectedEmployee.id}/send-reminder`);
      setAlertMessage({
        type: 'success',
        text: `Reminder email dispatched to ${selectedEmployee.email}!`,
      });
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (err) {
      console.error(err);
      setAlertMessage({ type: 'error', text: 'Failed to send reminder email.' });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle User Selection in Create Modal (Auto-fills employee details & preview assigned assets)
  const handleUserSelect = (userId) => {
    const selected = users.find((u) => String(u.id) === String(userId));
    if (selected) {
      setCreateForm((prev) => ({
        ...prev,
        user_management_id: selected.id,
        name: selected.name,
        email: selected.email,
        department: selected.department || 'Engineering',
        role: selected.role?.name || 'Staff Member',
      }));
    }
  };

  // Assets currently assigned to the selected user in create modal
  const userAssignedAssets = useMemo(() => {
    if (!createForm.user_management_id) return [];
    return allAssets.filter((a) => String(a.owner_id) === String(createForm.user_management_id));
  }, [allAssets, createForm.user_management_id]);

  // Submit Create Clearance
  const submitCreateClearance = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      const res = await axios.post('/asset-clearances', createForm);
      setAlertMessage({
        type: 'success',
        text: `Exit clearance case ${res.data?.data?.clearance_id} created! ${res.data?.email_sent ? 'Return instructions kit emailed to employee.' : ''}`,
      });
      await fetchData();
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
      setShowCreateModal(false);
      setCreateForm({
        user_management_id: '',
        name: '',
        email: '',
        department: 'Engineering',
        role: 'Staff Engineer',
        manager: 'Department Lead',
        last_working_day: new Date().toISOString().split('T')[0],
        exit_type: 'Resignation',
        priority: 'Medium',
        notes: 'Offboarding clearance initiated.',
        send_email: true,
      });
    } catch (err) {
      console.error(err);
      setAlertMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to create clearance case.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Alert Banner */}
      {alertMessage && (
        <div
          className={`p-4 rounded-xl border flex items-center justify-between transition-all ${
            alertMessage.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600'
              : 'bg-rose-500/10 border-rose-500/30 text-rose-600'
          }`}
        >
          <div className="flex items-center gap-3 text-sm font-medium">
            <Icon name={alertMessage.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={18} />
            <span>{alertMessage.text}</span>
          </div>
          <button onClick={() => setAlertMessage(null)} className="p-1 hover:opacity-75">
            <Icon name="X" size={16} />
          </button>
        </div>
      )}

      {/* Header and Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-border">
        <div>
          <h2 className="text-xl font-bold text-foreground">Asset Recovery & Exit Clearance</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Employee Offboarding: Hardware Asset Surrender, Credential Revocation, Licence Reassignment & Certificate Sign-Off
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Dual View Switcher */}
          <div className="flex items-center rounded-lg border border-border p-0.5 bg-muted/40 shadow-xs">
            <button
              type="button"
              onClick={() => handleSetViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'table'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Table View"
            >
              <Icon name="Table" size={14} />
              <span>Table</span>
            </button>
            <button
              type="button"
              onClick={() => handleSetViewMode('card')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'card'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Card View"
            >
              <Icon name="LayoutGrid" size={14} />
              <span>Cards</span>
            </button>
          </div>

          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={fetchData}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="UserMinus"
            iconPosition="left"
            onClick={() => setShowCreateModal(true)}
          >
            + Initiate Exit Clearance
          </Button>
        </div>
      </div>

      {/* Overview Stats Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase text-muted-foreground">Exit Clearance Queue</span>
            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Icon name="Users" size={18} />
            </span>
          </div>
          <div className="text-2xl font-bold text-foreground">
            {metrics.pendingCases} <span className="text-xs font-normal text-muted-foreground">Active Pending</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">{metrics.completedCases} clearances completed</div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase text-muted-foreground">1. Hardware Recovery</span>
            <span className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
              <Icon name="Laptop" size={18} />
            </span>
          </div>
          <div className="text-2xl font-bold text-foreground">
            {metrics.recoveredHardware} / {metrics.totalHardware}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{metrics.hardwarePct}% Physical Devices Recovered</div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase text-muted-foreground">2. Access Revoked</span>
            <span className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <Icon name="KeyRound" size={18} />
            </span>
          </div>
          <div className="text-2xl font-bold text-foreground">
            {metrics.revokedAccess} / {metrics.totalAccess}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{metrics.accessPct}% SSO, Cloud & Badges Disabled</div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase text-muted-foreground">3. Licences Reclaimed</span>
            <span className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <Icon name="Layers" size={18} />
            </span>
          </div>
          <div className="text-2xl font-bold text-emerald-500">
            ₹{metrics.recoveredLicenceCost.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            of ₹{metrics.totalLicenceCost.toLocaleString()} Reassigned / Reclaimed
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <div className="relative w-full max-w-sm">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search employee, ID, clearance tag, or department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-background border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center rounded-lg border border-border p-0.5 bg-muted/40 shadow-xs">
            <button
              type="button"
              onClick={() => handleSetViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'table'
                  ? 'bg-card text-foreground shadow-xs border border-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Table View"
            >
              <Icon name="Table" size={14} />
              <span>Table</span>
            </button>
            <button
              type="button"
              onClick={() => handleSetViewMode('card')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'card'
                  ? 'bg-card text-foreground shadow-xs border border-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Card View"
            >
              <Icon name="LayoutGrid" size={14} />
              <span>Cards</span>
            </button>
          </div>

          <div className="h-4 w-px bg-border hidden sm:block" />

          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filterStatus === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/40 text-muted-foreground hover:bg-muted'
            }`}
          >
            All Cases ({employees.length})
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filterStatus === 'pending'
                ? 'bg-amber-500 text-white'
                : 'bg-muted/40 text-muted-foreground hover:bg-muted'
            }`}
          >
            In Progress ({metrics.pendingCases})
          </button>
          <button
            onClick={() => setFilterStatus('completed')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filterStatus === 'completed'
                ? 'bg-emerald-500 text-white'
                : 'bg-muted/40 text-muted-foreground hover:bg-muted'
            }`}
          >
            Completed ({metrics.completedCases})
          </button>
        </div>
      </div>

      {/* Clearance Cases: Cards View vs Table View */}
      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEmployees.length === 0 ? (
            <div className="col-span-full bg-card border border-border rounded-xl p-8 text-center text-muted-foreground text-xs">
              No clearance cases found matching the criteria. Click "+ Initiate Exit Clearance" to begin.
            </div>
          ) : (
            filteredEmployees.map((emp) => {
              const hw = emp.hardware_assets || [];
              const recoveredHw = hw.filter((a) => a.status === 'Recovered').length;
              const acc = emp.access_items || [];
              const revokedAcc = acc.filter((a) => a.status === 'Revoked').length;
              const signoffs = emp.signoffs || {};
              const isCompleted = emp.status === 'Completed';

              return (
                <div
                  key={emp.id}
                  className="bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-base text-foreground">{emp.name}</h3>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-muted text-muted-foreground">
                            {emp.employee_id}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {emp.role || 'Staff'} • {emp.department}
                        </p>
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          isCompleted
                            ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                            : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                        }`}
                      >
                        {emp.status}
                      </span>
                    </div>

                    <div className="p-3 bg-muted/30 rounded-lg text-xs space-y-1 mb-4">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Clearance Ref:</span>
                        <span className="font-mono font-semibold text-primary">{emp.clearance_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Exit Date:</span>
                        <span className="font-medium text-foreground">
                          {emp.last_working_day
                            ? new Date(emp.last_working_day).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })
                            : 'Immediate'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Exit Type:</span>
                        <span className="font-medium text-foreground">{emp.exit_type}</span>
                      </div>
                    </div>

                    {/* Progress Items */}
                    <div className="space-y-2 mb-4 text-xs">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-muted-foreground flex items-center gap-1.5">
                            <Icon name="Laptop" size={12} className="text-blue-500" />
                            Hardware Assets
                          </span>
                          <span className="font-semibold text-foreground">
                            {recoveredHw}/{hw.length} Recovered
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                          <div
                            className="bg-blue-500 h-1.5 rounded-full"
                            style={{ width: `${hw.length ? (recoveredHw / hw.length) * 100 : 0}%` }}
                          />
                        </div>
                      </div>

                      {acc.length > 0 && (
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-muted-foreground flex items-center gap-1.5">
                              <Icon name="KeyRound" size={12} className="text-amber-500" />
                              Credentials & Accounts
                            </span>
                            <span className="font-semibold text-foreground">
                              {revokedAcc}/{acc.length} Revoked
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-amber-500 h-1.5 rounded-full"
                              style={{ width: `${acc.length ? (revokedAcc / acc.length) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {(emp.licences || []).length > 0 && (
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-muted-foreground flex items-center gap-1.5">
                              <Icon name="Layers" size={12} className="text-emerald-500" />
                              Software Licences
                            </span>
                            <span className="font-semibold text-foreground">
                              {(emp.licences || []).filter((l) => l.status !== 'Pending').length}/{(emp.licences || []).length} Reassigned
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-emerald-500 h-1.5 rounded-full"
                              style={{
                                width: `${
                                  (emp.licences || []).length
                                    ? ((emp.licences || []).filter((l) => l.status !== 'Pending').length / (emp.licences || []).length) * 100
                                    : 0
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Department Sign-Off Badges */}
                    <div className="flex items-center gap-2 mb-4">
                      <span
                        className={`flex-1 py-1 px-1.5 rounded text-[10px] text-center font-bold border ${
                          signoffs.it
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                            : 'bg-muted border-border text-muted-foreground'
                        }`}
                      >
                        IT {signoffs.it ? '✓' : '⏳'}
                      </span>
                      <span
                        className={`flex-1 py-1 px-1.5 rounded text-[10px] text-center font-bold border ${
                          signoffs.hr
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                            : 'bg-muted border-border text-muted-foreground'
                        }`}
                      >
                        HR {signoffs.hr ? '✓' : '⏳'}
                      </span>
                      <span
                        className={`flex-1 py-1 px-1.5 rounded text-[10px] text-center font-bold border ${
                          signoffs.finance
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                            : 'bg-muted border-border text-muted-foreground'
                        }`}
                      >
                        Finance {signoffs.finance ? '✓' : '⏳'}
                      </span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-border flex items-center justify-between gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleOpenChecklist(emp)}
                    >
                      Manage Checklist
                    </Button>
                    {isCompleted && (
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Award"
                        onClick={() => {
                          setSelectedEmployee(emp);
                          setShowCertificateModal(true);
                        }}
                      >
                        Certificate
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        /* Clearance Cases Table View */
        <div className="bg-card border border-border rounded-xl shadow-xs overflow-hidden">
          <div className="p-4 border-b border-border bg-muted/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-sm text-foreground">Clearance Cases Table</h3>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary/10 text-primary">
                {filteredEmployees.length} Cases
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              Real-time synchronization with enterprise assets & credentials
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] font-semibold tracking-wider border-b border-border">
                <tr>
                  <th className="py-3 px-4">Employee & Case ID</th>
                  <th className="py-3 px-4">Department & Role</th>
                  <th className="py-3 px-4">Exit Details</th>
                  <th className="py-3 px-4">Hardware Recovery</th>
                  <th className="py-3 px-4">Credentials & Access</th>
                  <th className="py-3 px-4 text-center">Sign-Offs</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-muted-foreground">
                      No clearance cases found matching the criteria. Click "+ Initiate Exit Clearance" to begin.
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((emp) => {
                    const hw = emp.hardware_assets || [];
                    const recoveredHw = hw.filter((a) => a.status === 'Recovered').length;
                    const acc = emp.access_items || [];
                    const revokedAcc = acc.filter((a) => a.status === 'Revoked').length;
                    const signoffs = emp.signoffs || {};
                    const isCompleted = emp.status === 'Completed';
                    const hwPct = hw.length ? Math.round((recoveredHw / hw.length) * 100) : 100;
                    const accPct = acc.length ? Math.round((revokedAcc / acc.length) * 100) : 100;

                    return (
                      <tr key={emp.id} className="hover:bg-muted/30 transition-colors">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-foreground text-xs">{emp.name}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-muted text-muted-foreground">
                              {emp.employee_id}
                            </span>
                            <span className="text-[11px] font-mono font-semibold text-primary">
                              {emp.clearance_id}
                            </span>
                          </div>
                          {emp.email && (
                            <div className="text-[11px] text-muted-foreground truncate max-w-[180px] mt-0.5">
                              {emp.email}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium text-foreground">{emp.department}</div>
                          <div className="text-[11px] text-muted-foreground">{emp.role || 'Staff'}</div>
                          {emp.manager && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">Lead: {emp.manager}</div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-foreground font-medium">
                            {emp.last_working_day
                              ? new Date(emp.last_working_day).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })
                              : 'Immediate'}
                          </div>
                          <div className="text-[11px] text-muted-foreground">{emp.exit_type}</div>
                          {emp.priority && (
                            <span
                              className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                emp.priority === 'Critical'
                                  ? 'bg-rose-500/10 text-rose-500'
                                  : emp.priority === 'High'
                                  ? 'bg-amber-500/10 text-amber-500'
                                  : 'bg-blue-500/10 text-blue-500'
                              }`}
                            >
                              {emp.priority}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 min-w-[160px]">
                          {hw.length === 0 ? (
                            <span className="text-muted-foreground text-[11px] italic">N/A (No Hardware)</span>
                          ) : (
                            <>
                              <div className="flex justify-between text-[11px] mb-1">
                                <span className="font-semibold text-foreground">
                                  {recoveredHw}/{hw.length} Recovered
                                </span>
                                <span className="text-muted-foreground font-medium">{hwPct}%</span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="bg-blue-500 h-1.5 rounded-full transition-all"
                                  style={{ width: `${hwPct}%` }}
                                />
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-1 truncate max-w-[180px]">
                                {hw.map((a) => a.name || a.tag).slice(0, 2).join(', ')}
                                {hw.length > 2 ? ` +${hw.length - 2}` : ''}
                              </div>
                            </>
                          )}
                        </td>
                        <td className="py-3 px-4 min-w-[140px]">
                          {acc.length === 0 ? (
                            <span className="text-muted-foreground text-[11px] italic">N/A (No Credentials)</span>
                          ) : (
                            <>
                              <div className="flex justify-between text-[11px] mb-1">
                                <span className="font-semibold text-foreground">
                                  {revokedAcc}/{acc.length} Revoked
                                </span>
                                <span className="text-muted-foreground font-medium">{accPct}%</span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="bg-amber-500 h-1.5 rounded-full transition-all"
                                  style={{ width: `${accPct}%` }}
                                />
                              </div>
                            </>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <span
                              title={signoffs.it ? 'IT Clearance Granted' : 'IT Clearance Pending'}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                                signoffs.it
                                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                                  : 'bg-muted border-border text-muted-foreground'
                              }`}
                            >
                              IT {signoffs.it ? '✓' : '⏳'}
                            </span>
                            <span
                              title={signoffs.hr ? 'HR Clearance Granted' : 'HR Clearance Pending'}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                                signoffs.hr
                                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                                  : 'bg-muted border-border text-muted-foreground'
                              }`}
                            >
                              HR {signoffs.hr ? '✓' : '⏳'}
                            </span>
                            <span
                              title={signoffs.finance ? 'Finance Clearance Granted' : 'Finance Clearance Pending'}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                                signoffs.finance
                                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                                  : 'bg-muted border-border text-muted-foreground'
                              }`}
                            >
                              FIN {signoffs.finance ? '✓' : '⏳'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              isCompleted
                                ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                                : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                            }`}
                          >
                            {emp.status}
                          </span>
                          {emp.certificate_number && (
                            <div className="text-[10px] font-mono text-muted-foreground mt-0.5 truncate max-w-[130px]">
                              {emp.certificate_number}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              variant="outline"
                              size="xs"
                              iconName="ListChecks"
                              iconPosition="left"
                              onClick={() => handleOpenChecklist(emp)}
                            >
                              Checklist
                            </Button>
                            {isCompleted && (
                              <Button
                                variant="default"
                                size="xs"
                                iconName="Award"
                                onClick={() => {
                                  setSelectedEmployee(emp);
                                  setShowCertificateModal(true);
                                }}
                              >
                                Cert
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Checklist Drawer */}
      {showDrawer && selectedEmployee && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/60 flex justify-end">
          <div className="w-full max-w-xl bg-card h-full shadow-2xl flex flex-col border-l border-border animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="p-5 border-b border-border flex items-center justify-between bg-muted/20">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-lg text-foreground">{selectedEmployee.name}</h3>
                  <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-primary/10 text-primary">
                    {selectedEmployee.clearance_id}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {selectedEmployee.employee_id} • {selectedEmployee.department} • Exit: {selectedEmployee.exit_type}
                </p>
              </div>
              <button
                onClick={() => setShowDrawer(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Quick Email Actions Bar */}
            <div className="p-3 bg-muted/40 border-b border-border flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Notifications & Courier:</span>
              <div className="flex items-center gap-2">
                <Button
                  size="xs"
                  variant="outline"
                  iconName="Package"
                  iconPosition="left"
                  onClick={handleSendReturnKit}
                  disabled={actionLoading}
                >
                  Send Return Kit
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  iconName="Mail"
                  iconPosition="left"
                  onClick={handleSendReminder}
                  disabled={actionLoading}
                >
                  Send Reminder
                </Button>
              </div>
            </div>

            {/* Content Sections */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Section 1: Hardware Assets */}
              <div className="bg-muted/20 border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-md bg-blue-500/10 text-blue-500 flex items-center justify-center">
                      <Icon name="Laptop" size={14} />
                    </span>
                    <h4 className="font-bold text-sm text-foreground">1. Recover Hardware Assets</h4>
                  </div>
                  <span className="text-xs font-semibold text-primary">
                    {(selectedEmployee.hardware_assets || []).filter((a) => a.status === 'Recovered').length}/
                    {(selectedEmployee.hardware_assets || []).length} Recovered
                  </span>
                </div>

                <div className="space-y-2">
                  {(selectedEmployee.hardware_assets || []).map((asset) => {
                    const isRecovered = asset.status === 'Recovered';
                    return (
                      <div
                        key={asset.id || asset.tag}
                        className="p-3 bg-card border border-border rounded-lg flex items-center justify-between gap-3 hover:border-primary/40 transition-colors"
                      >
                        <div
                          onClick={() => toggleAssetRecovery(asset.id || asset.tag, asset.condition || 'Optimal')}
                          className="flex items-center gap-2.5 cursor-pointer flex-1"
                        >
                          <input
                            type="checkbox"
                            checked={isRecovered}
                            onChange={() => {}}
                            className="rounded border-border text-primary cursor-pointer"
                          />
                          <div>
                            <div className="font-semibold text-foreground text-xs">{asset.name}</div>
                            <div className="text-[11px] text-muted-foreground font-mono">
                              Tag: {asset.tag || asset.asset_id} • SN: {asset.serial || 'N/A'}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <select
                            value={asset.condition || 'Optimal'}
                            onChange={(e) => toggleAssetRecovery(asset.id || asset.tag, e.target.value)}
                            className="text-[11px] bg-background border border-border rounded px-1.5 py-0.5 text-foreground"
                          >
                            <option value="Optimal">Optimal</option>
                            <option value="Good">Good</option>
                            <option value="Fair">Fair</option>
                            <option value="Damaged">Damaged</option>
                          </select>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              isRecovered ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'
                            }`}
                          >
                            {asset.status}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Section 2: System Access & Credentials - only shown if added credentials exist */}
              {(selectedEmployee.access_items || []).length > 0 && (
                <div className="bg-muted/20 border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-amber-500/10 text-amber-500 flex items-center justify-center">
                        <Icon name="KeyRound" size={14} />
                      </span>
                      <h4 className="font-bold text-sm text-foreground">Revoke System Access & Credentials</h4>
                    </div>
                    <span className="text-xs font-semibold text-amber-500">
                      {(selectedEmployee.access_items || []).filter((a) => a.status === 'Revoked').length}/
                      {(selectedEmployee.access_items || []).length} Revoked
                    </span>
                  </div>

                  <div className="space-y-2">
                    {(selectedEmployee.access_items || []).map((acc) => {
                      const isRevoked = acc.status === 'Revoked';
                      return (
                        <div
                          key={acc.id}
                          onClick={() => toggleAccessRevocation(acc.id)}
                          className="p-3 bg-card border border-border rounded-lg flex items-center justify-between cursor-pointer hover:border-primary/40 transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={isRevoked}
                              onChange={() => {}}
                              className="rounded border-border text-primary"
                            />
                            <div>
                              <div className="font-semibold text-foreground text-xs">{acc.name}</div>
                              <div className="text-[11px] text-muted-foreground">
                                {acc.category} • {acc.type}
                              </div>
                            </div>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              isRevoked ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'
                            }`}
                          >
                            {acc.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Section 3: Software Licences - only shown if asset software & licences exist */}
              {(selectedEmployee.licences || []).length > 0 && (
                <div className="bg-muted/20 border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                        <Icon name="Layers" size={14} />
                      </span>
                      <h4 className="font-bold text-sm text-foreground">Reassign Software Licences</h4>
                    </div>
                    <span className="text-xs text-emerald-500 font-bold">
                      ₹
                      {(selectedEmployee.licences || [])
                        .reduce((acc, l) => acc + (l.status !== 'Pending' ? Number(l.cost || 0) : 0), 0)
                        .toLocaleString()}{' '}
                      Saved
                    </span>
                  </div>

                  <div className="space-y-2">
                    {(selectedEmployee.licences || []).map((lic) => {
                      const isReclaimed = lic.status !== 'Pending';
                      return (
                        <div
                          key={lic.id}
                          onClick={() => toggleLicenceStatus(lic.id)}
                          className="p-3 bg-card border border-border rounded-lg flex items-center justify-between cursor-pointer hover:border-primary/40 transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={isReclaimed}
                              onChange={() => {}}
                              className="rounded border-border text-primary"
                            />
                            <div>
                              <div className="font-semibold text-foreground text-xs">{lic.software}</div>
                              <div className="text-[11px] text-muted-foreground font-mono">
                                Key: {lic.licenceKey} • ₹{Number(lic.cost || 0).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              isReclaimed ? 'bg-emerald-500/10 text-emerald-500' : 'bg-blue-500/10 text-blue-500'
                            }`}
                          >
                            {lic.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Department Sign-Offs */}
              <div className="bg-muted/20 border border-border rounded-xl p-4">
                <h4 className="font-bold text-sm text-foreground mb-3">Inter-Department Clearance Sign-offs</h4>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => toggleSignoff('it')}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      selectedEmployee.signoffs?.it
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500 font-bold'
                        : 'bg-card border-border text-muted-foreground hover:border-primary'
                    }`}
                  >
                    <Icon
                      name={selectedEmployee.signoffs?.it ? 'CheckCircle2' : 'Circle'}
                      size={18}
                      className="mx-auto mb-1"
                    />
                    <div className="text-xs">IT Clearance</div>
                  </button>

                  <button
                    onClick={() => toggleSignoff('hr')}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      selectedEmployee.signoffs?.hr
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500 font-bold'
                        : 'bg-card border-border text-muted-foreground hover:border-primary'
                    }`}
                  >
                    <Icon
                      name={selectedEmployee.signoffs?.hr ? 'CheckCircle2' : 'Circle'}
                      size={18}
                      className="mx-auto mb-1"
                    />
                    <div className="text-xs">Security / HR</div>
                  </button>

                  <button
                    onClick={() => toggleSignoff('finance')}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      selectedEmployee.signoffs?.finance
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500 font-bold'
                        : 'bg-card border-border text-muted-foreground hover:border-primary'
                    }`}
                  >
                    <Icon
                      name={selectedEmployee.signoffs?.finance ? 'CheckCircle2' : 'Circle'}
                      size={18}
                      className="mx-auto mb-1"
                    />
                    <div className="text-xs">Finance Clearance</div>
                  </button>
                </div>
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-border flex items-center justify-between gap-3">
              <Button variant="outline" size="sm" onClick={() => setShowDrawer(false)}>
                Close Checklist
              </Button>
              <div className="flex items-center gap-2">
                {selectedEmployee.certificate_number && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="FileCheck"
                    onClick={() => setShowCertificateModal(true)}
                  >
                    View Certificate
                  </Button>
                )}
                <Button
                  variant="default"
                  size="sm"
                  iconName="Award"
                  iconPosition="left"
                  onClick={handleCompleteClearance}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Finalizing...' : 'Complete Clearance Sign-off'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Initiate Exit Clearance Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-lg rounded-xl border border-border shadow-2xl p-6">
            <div className="flex items-center justify-between pb-3 border-b border-border mb-4">
              <div>
                <h3 className="font-bold text-lg text-foreground">Initiate Employee Exit Clearance</h3>
                <p className="text-xs text-muted-foreground">
                  Select employee to automatically fetch their assigned assets from database.
                </p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>

            <form onSubmit={submitCreateClearance} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold mb-1 text-foreground">Select System User</label>
                <select
                  value={createForm.user_management_id}
                  onChange={(e) => handleUserSelect(e.target.value)}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                  required
                >
                  <option value="">-- Choose Employee / User --</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email || u.department || 'User'})
                    </option>
                  ))}
                </select>
              </div>

              {/* Live Assets Preview for selected user */}
              {userAssignedAssets.length > 0 ? (
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="font-bold text-primary mb-1">
                    ✓ Found {userAssignedAssets.length} Hardware Assets Assigned in Database:
                  </div>
                  <ul className="list-disc pl-4 space-y-0.5 text-[11px] text-muted-foreground">
                    {userAssignedAssets.map((a) => (
                      <li key={a.id}>
                        {a.name || `${a.brand} ${a.model}`} ({a.asset_id}) - {a.category?.name || 'Hardware'}
                        {(a.other_software || a.software_licence?.software) && (
                          <span className="text-emerald-500 font-semibold ml-1">
                            • Software: {a.software_licence?.software || a.other_software}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : createForm.user_management_id ? (
                <div className="p-2.5 bg-muted/40 border border-border rounded-lg text-muted-foreground text-[11px]">
                  ℹ No hardware assets currently assigned to this user in database.
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold mb-1 text-foreground">Employee Full Name</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1 text-foreground">Corporate Email</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold mb-1 text-foreground">Department</label>
                  <input
                    type="text"
                    value={createForm.department}
                    onChange={(e) => setCreateForm({ ...createForm, department: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1 text-foreground">Last Working Day</label>
                  <input
                    type="date"
                    value={createForm.last_working_day}
                    onChange={(e) => setCreateForm({ ...createForm, last_working_day: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold mb-1 text-foreground">Exit Type</label>
                  <select
                    value={createForm.exit_type}
                    onChange={(e) => setCreateForm({ ...createForm, exit_type: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                  >
                    <option value="Resignation">Resignation</option>
                    <option value="Retirement">Retirement</option>
                    <option value="Contract End">Contract End</option>
                    <option value="Termination">Termination</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold mb-1 text-foreground">Priority</label>
                  <select
                    value={createForm.priority}
                    onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-semibold mb-1 text-foreground">Handover Notes</label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  rows={2}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                />
              </div>

              <div className="p-3 bg-muted/40 rounded-lg space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="createEmailCheck"
                    checked={createForm.send_email}
                    onChange={(e) => setCreateForm({ ...createForm, send_email: e.target.checked })}
                    className="rounded border-border text-primary"
                  />
                  <label htmlFor="createEmailCheck" className="text-foreground font-semibold">
                    Send Pre-Paid Return Kit Email Instructions
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground pl-5">
                  Sends hardware equipment return instructions and courier pickup label to employee email.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" type="submit" disabled={actionLoading}>
                  {actionLoading ? 'Creating...' : 'Initiate Offboarding Case'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Official Certificate Modal */}
      {showCertificateModal && selectedEmployee && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-2xl rounded-2xl border border-border shadow-2xl p-8 relative overflow-hidden">
            <button
              onClick={() => setShowCertificateModal(false)}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={20} />
            </button>

            {/* Certificate Header */}
            <div className="text-center pb-6 border-b border-border">
              <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-500 mb-2">
                Official Document • Kerol Systems
              </div>
              <h2 className="text-2xl font-black text-foreground tracking-tight">
                IT & ASSET EXIT CLEARANCE CERTIFICATE
              </h2>
              <p className="text-xs text-muted-foreground mt-1 font-mono">
                Certificate ID: {selectedEmployee.certificate_number || 'CLR-CERT-2026-ACTIVE'}
              </p>
            </div>

            {/* Certificate Body */}
            <div className="py-6 space-y-6 text-xs text-foreground">
              <p className="leading-relaxed text-muted-foreground">
                This certifies that employee <strong className="text-foreground">{selectedEmployee.name}</strong> (Employee ID:{' '}
                <span className="font-mono">{selectedEmployee.employee_id}</span>, Department:{' '}
                <strong>{selectedEmployee.department}</strong>) has successfully surrendered all company-issued hardware equipment,
                relinquished access to corporate repositories and directory credentials, and completed formal exit sign-offs.
              </p>

              {/* Hardware Summary */}
              <div className="border border-border rounded-xl p-4 bg-muted/20">
                <h4 className="font-bold text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Verified Surrendered Hardware
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {(selectedEmployee.hardware_assets || []).map((a) => (
                    <div key={a.id || a.tag} className="flex items-center gap-2 p-2 bg-card rounded border border-border">
                      <Icon name="CheckCircle2" size={14} className="text-emerald-500 shrink-0" />
                      <div className="truncate">
                        <div className="font-semibold">{a.name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{a.tag}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sign-off Stamps */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 border border-emerald-500/30 bg-emerald-500/5 rounded-xl">
                  <div className="text-emerald-600 font-bold">✓ IT CLEARANCE</div>
                  <div className="text-[10px] text-muted-foreground mt-1">Hardware & MDM Cleared</div>
                </div>
                <div className="p-3 border border-emerald-500/30 bg-emerald-500/5 rounded-xl">
                  <div className="text-emerald-600 font-bold">✓ SECURITY / HR</div>
                  <div className="text-[10px] text-muted-foreground mt-1">SSO & Cloud Revoked</div>
                </div>
                <div className="p-3 border border-emerald-500/30 bg-emerald-500/5 rounded-xl">
                  <div className="text-emerald-600 font-bold">✓ FINANCE DISCHARGE</div>
                  <div className="text-[10px] text-muted-foreground mt-1">Asset Books Cleared</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-6 border-t border-border flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">
                Issued by Kerol Systems Asset Operations Depot
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  Print Certificate
                </Button>
                <Button variant="default" size="sm" onClick={() => setShowCertificateModal(false)}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetRecoveryView;
